import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import { lifeSpan, formatNumber } from "../lib/format";
import { ENV_KEYS, SPECIES_KEYS } from "../lib/constants";

export default function ListPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const environment = params.get("environment") || "";
  const species = params.get("species") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (p, append) => {
      setLoading(true);
      try {
        const { data } = await api.get("/memorials", { params: { q: params.get("q") || "", environment, species, page: p, limit: 24 } });
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
        setTotal(data.total);
        setPage(p);
      } finally {
        setLoading(false);
      }
    },
    [params, environment, species]
  );
  useEffect(() => {
    load(1, false);
  }, [load]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next);
  };

  return (
    <main id="main" className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-24" data-testid="list-page">
      <h1 className="font-heading text-4xl sm:text-5xl">{t("list.title")}</h1>
      <p className="text-[hsl(var(--muted-foreground))] mt-4 max-w-2xl">{t("list.lead")}</p>
      <form role="search" className="mt-10 grid md:grid-cols-[1fr_auto_auto_auto] gap-3" onSubmit={(e) => { e.preventDefault(); update("q", q.trim()); }}>
        <label className="relative">
          <span className="sr-only">{t("list.placeholder")}</span>
          <Search size={16} className="absolute start-4 top-1/2 -translate-y-1/2 opacity-50" aria-hidden="true" />
          <input className="field-input ps-11" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("list.placeholder")} data-testid="list-search-input" />
        </label>
        <select className="field-input" value={environment} onChange={(e) => update("environment", e.target.value)} aria-label={t("wizard.steps.env")} data-testid="list-env-select">
          <option value="">{t("list.all_envs")}</option>
          {ENV_KEYS.map((k) => <option key={k} value={k}>{t(`env.${k}.name`)}</option>)}
        </select>
        <select className="field-input" value={species} onChange={(e) => update("species", e.target.value)} aria-label={t("wizard.species")} data-testid="list-species-select">
          <option value="">{t("list.all_species")}</option>
          {SPECIES_KEYS.map((k) => <option key={k} value={k}>{t(`species.${k}`)}</option>)}
        </select>
        <button className="btn-primary" type="submit" data-testid="list-search-btn">{t("common.search")}</button>
      </form>
      <p className="mt-8 text-sm text-[hsl(var(--muted-foreground))]" aria-live="polite" data-testid="list-results-count">{t("list.results", { count: formatNumber(total) })}</p>
      {!loading && items.length === 0 && <p className="mt-10 text-lg" data-testid="list-empty">{t("list.empty")}</p>}
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
        {items.map((m) => (
          <li key={m.slug}>
            <Link to={`/m/${m.slug}`} className="flex gap-4 items-center rounded-3xl border bg-[hsl(var(--card))] p-4 hover:border-[hsl(var(--accent))] hover:shadow-lg transition-shadow" data-testid={`list-item-${m.slug}`}>
              {m.photo ? <img src={assetUrl(m.photo.thumb)} alt="" loading="lazy" className="h-20 w-20 rounded-2xl object-cover" /> : <span className="h-20 w-20 rounded-2xl bg-[hsl(var(--secondary))]" />}
              <span className="min-w-0">
                <span className="block font-heading text-xl truncate" dir="auto">{m.name}</span>
                {m.surname && <span className="block text-sm text-[hsl(var(--muted-foreground))] truncate" dir="auto">{m.surname}</span>}
                <span className="block text-xs text-[hsl(var(--muted-foreground))] mt-1">{lifeSpan(m)}</span>
                <span className="block text-xs mt-1">{t(`env.${m.environment}.name`)}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {items.length < total && (
        <button className="btn-ghost mt-10" onClick={() => load(page + 1, true)} disabled={loading} data-testid="list-load-more-btn">{t("list.load_more")}</button>
      )}
    </main>
  );
}
